import { useEffect, useState } from "react";
import { Plus, Trash2, Power, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

export default function Bands() {
  const [bands, setBands] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [tagline, setTagline] = useState("");

  const load = () => api.get("/admin/bands").then(({ data }) => setBands(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name || !code) { toast.error("Name and code are required"); return; }
    try {
      await api.post("/admin/bands", { name, code, tagline });
      toast.success("Band created");
      setOpen(false); setName(""); setCode(""); setTagline("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const toggleActive = async (b) => {
    await api.put(`/admin/bands/${b.id}`, { active: !b.active });
    toast.success(b.active ? "Band suspended" : "Band reactivated");
    load();
  };

  const toggleRoadDay = async (b) => {
    await api.put(`/admin/bands/${b.id}`, { road_day_active: !b.road_day_active });
    toast.success(`Road Day ${b.road_day_active ? "ended" : "started"} for ${b.name}`);
    load();
  };

  const del = async (b) => {
    if (!window.confirm(`Delete ${b.name}? This removes its safe points, benefits and updates, and unassigns participants.`)) return;
    await api.delete(`/admin/bands/${b.id}`);
    toast.success("Band deleted");
    load();
  };

  if (!bands) return <Spinner />;

  return (
    <Page>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head font-extrabold text-3xl">Bands</h1>
          <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>{bands.length} bands on the platform</p>
        </div>
        <button data-testid="admin-band-add-toggle" onClick={() => setOpen(!open)} className="cs-tap magenta-grad rounded-xl px-4 py-2.5 text-white font-semibold text-sm flex items-center gap-2">
          <Plus size={18} /> New Band
        </button>
      </div>

      {open && (
        <div className="cs-card p-5 mt-5 rise">
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="admin-band-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Band name" className="ad-input" />
            <input data-testid="admin-band-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BAND CODE" className="ad-input font-mono" />
            <input data-testid="admin-band-tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Tagline (optional)" className="ad-input" />
          </div>
          <button data-testid="admin-band-save" onClick={create} className="cs-tap mt-3 magenta-grad rounded-xl px-5 py-2.5 text-white font-bold text-sm">Create Band</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {bands.map((b) => (
          <div key={b.id} data-testid={`admin-band-${b.id}`} className="cs-card p-5" style={{ opacity: b.active ? 1 : 0.6 }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-head font-bold text-lg">{b.name}</p>
                <p className="text-sm font-mono" style={{ color: "var(--cs-magenta)" }}>{b.code}</p>
                <p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>{b.tagline || "—"} · {b.participants} participants</p>
              </div>
              <span className="text-[11px] rounded-full px-2 py-0.5 font-semibold" style={{ background: b.active ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", color: b.active ? "var(--cs-green)" : "var(--cs-red)" }}>
                {b.active ? "Active" : "Suspended"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button data-testid={`admin-band-roadday-${b.id}`} onClick={() => toggleRoadDay(b)}
                className="cs-tap rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-1.5"
                style={{ background: b.road_day_active ? "rgba(224,26,79,0.15)" : "var(--cs-card-hi)", color: b.road_day_active ? "var(--cs-magenta)" : "#fff", border: "1px solid var(--cs-border)" }}>
                <Zap size={14} /> {b.road_day_active ? "Road Day ON" : "Road Day OFF"}
              </button>
              <button data-testid={`admin-band-toggle-${b.id}`} onClick={() => toggleActive(b)}
                className="cs-tap rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-1.5" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}>
                <Power size={14} /> {b.active ? "Suspend" : "Reactivate"}
              </button>
              <button data-testid={`admin-band-delete-${b.id}`} onClick={() => del(b)}
                className="cs-tap rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-1.5" style={{ background: "rgba(239,68,68,0.12)", color: "var(--cs-red)", border: "1px solid rgba(239,68,68,0.3)" }}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      <style>{`.ad-input{height:46px;border-radius:.65rem;padding:0 .9rem;background:var(--cs-card-hi);border:1px solid var(--cs-border);color:#fff;outline:none;font-size:15px}.ad-input:focus{border-color:var(--cs-magenta)}`}</style>
    </Page>
  );
}
